import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Megaphone, Save, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AnnouncementAdmin = () => {
  const [announcement, setAnnouncement] = useState({
    enabled: false,
    title: '',
    content: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadAnnouncement();
  }, []);

  const loadAnnouncement = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/announcement`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncement(response.data);
    } catch (error) {
      console.error('Error loading announcement:', error);
      toast.error('Erreur lors du chargement de l\'annonce');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('adminToken');
      
      await axios.put(`${API}/announcement`, announcement, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Annonce mise à jour avec succès');
    } catch (error) {
      console.error('Error saving announcement:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = () => {
    setAnnouncement(prev => ({ ...prev, enabled: !prev.enabled }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-[#FF6B35] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-[#121212] border-white/10">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-[#FF6B35]" />
                Gestion de l'Annonce
              </CardTitle>
              <CardDescription className="text-gray-400 mt-2">
                Gérez l'annonce qui s'affiche aux visiteurs du site
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-400">
                Statut :
              </span>
              <button
                onClick={handleToggle}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  announcement.enabled ? 'bg-[#FF6B35]' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    announcement.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm font-semibold ${
                announcement.enabled ? 'text-[#FF6B35]' : 'text-gray-400'
              }`}>
                {announcement.enabled ? 'Activée' : 'Désactivée'}
              </span>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Form */}
      <Card className="bg-[#121212] border-white/10">
        <CardContent className="pt-6 space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-white">
              Titre de l'annonce
            </Label>
            <Input
              id="title"
              value={announcement.title}
              onChange={(e) => setAnnouncement(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Ex: Avis Important"
              className="bg-black/50 border-white/10 text-white focus:border-[#FF6B35]"
            />
          </div>

          {/* Content */}
          <div className="space-y-2">
            <Label htmlFor="content" className="text-white">
              Contenu de l'annonce
            </Label>
            <Textarea
              id="content"
              value={announcement.content}
              onChange={(e) => setAnnouncement(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Rédigez votre message ici..."
              rows={12}
              className="bg-black/50 border-white/10 text-white focus:border-[#FF6B35] resize-none"
            />
            <p className="text-sm text-gray-400">
              Le texte sera affiché tel quel. Les retours à la ligne seront respectés.
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#FF6B35] hover:bg-[#FF8555] text-white"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les modifications
                </>
              )}
            </Button>
            <Button
              onClick={loadAnnouncement}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Recharger
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      {announcement.content && (
        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-sm">Aperçu</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="glass border-2 border-[#FF6B35]/30 rounded-xl p-6">
              {announcement.title && (
                <h3 className="text-xl font-black mb-4 text-[#FF6B35]">
                  {announcement.title}
                </h3>
              )}
              <div className="text-gray-300 whitespace-pre-line leading-relaxed text-sm">
                {announcement.content}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
